import EmptyImg from "@/assets/content-media-folder.svg";
import { Input } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { FaAngleRight } from "react-icons/fa6";
import { IoIosSearch } from "react-icons/io";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KEYS_ELEARNING_SEARCH_PARAMS } from "..";
import { useQuizList } from "../hooks/useQuizList";
import { LessonPlaceholder } from "./LessonComponent";
import { QuizModal } from "./QuizModal";

const tableHeight = `h-[calc(100vh-52px-60px-48px-1px-32px-20px)]`; // 100vh - header - title - tab -inkbar - padding - search

export const Quizzes = () => {
  const navigate = useNavigate();
  const isAtBottomRef = useRef(false);
  const isInitialMount = useRef(true);
  const refQuiz = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const currentSearch = params[KEYS_ELEARNING_SEARCH_PARAMS.SEARCH] || "";

  const [inputValue, setInputValue] = useState<string>(currentSearch);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const {
    data: quizList,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useQuizList(currentSearch);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({
        [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]: inputValue?.trim(),
      });
    }
  };

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm?.trim()) {
      updateSearchParams({
        [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]:
          debouncedSearchTerm?.trim() || "",
      });
    } else {
      updateSearchParams({ [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  const checkIfAtBottom = () => {
    if (!refQuiz.current) return;
    const { scrollTop, scrollHeight, clientHeight } = refQuiz.current;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5; // Ngưỡng 5px

    if (isAtBottom && !isAtBottomRef.current) {
      isAtBottomRef.current = true;
      if (hasNextPage) {
        fetchNextPage();
      }
    } else if (!isAtBottom) {
      isAtBottomRef.current = false;
    }
  };

  const quizListFlat = quizList?.pages.flatMap((page) => page.data) || [];

  if (isLoading && quizList === undefined) {
    return (
      <section className="py-4">
        {Array(3)
          .fill(1)
          .map((_, idx) => (
            <div key={idx} className="space-y-4 w-full animate-pulse">
              <div className="flex items-center justify-between h-4 w-96 bg-light_grey  rounded-full" />
              <div className="grid grid-cols-5 gap-6">
                {Array(5)
                  .fill(1)
                  .map((_, index) => (
                    <LessonPlaceholder key={`${idx}${index}`} />
                  ))}
              </div>
            </div>
          ))}
      </section>
    );
  }

  return (
    <>
      <section className={cn("space-y-4 relative w-full h-full py-4")}>
        <div className="absolute -top-11 right-0 z-10  bg-white">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="検索"
            icon={<IoIosSearch className="size-4" />}
            className="!h-10 w-80"
            onKeyDown={handleKeyDown}
          />
        </div>
        <div
          className={cn(
            "overflow-y-scroll visible-scrollbar space-y-6",
            tableHeight,
          )}
          ref={refQuiz}
          onScroll={checkIfAtBottom}
        >
          {!quizListFlat?.length && (
            <div className="flex flex-col items-center text-grey-500 text-sm gap-6 py-12">
              <img src={EmptyImg} className="w-48 h-48" />
              <p>まだデータがありません。テストを作成しましょう！</p>
            </div>
          )}
          {quizListFlat.map(({ id, title, quizzes }) => (
            <div key={id} className="space-y-4 text-dark w-full">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold leading-6 truncate max-w-[500px]">
                  {title}
                </h2>
                <button
                  className="flex flex-grow-0 items-center justify-center gap-2 text-cyan hover:underline"
                  onClick={() => navigate(`/e-learning/quiz-category/${id}`)}
                >
                  <p className="font-semibold leading-5.5 text-sm">全て表示</p>
                  <FaAngleRight className="w-5" />
                </button>
              </div>
              {/* lesson  */}
              <div className="grid grid-cols-5 gap-6">
                {quizzes.map((quiz) => (
                  <QuizModal key={quiz.id} quiz={quiz} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};
